import { LOGIC, SAVE } from "@/data/tunables";
import type { GameState } from "@/state/GameState";

/**
 * The RULED game-state capture (v0.12 RFC §3.3): ONE compact snapshot
 * shape shared by the grievance payload and every telemetry beacon —
 * one builder, two consumers, so the shapes can never drift. Pure and
 * Phaser-free (headless-tested); ≤~600B serialized.
 */

export interface RunSnapshot {
  tick: number;
  funding: number;
  rep: number;
  heat: number;
  dea: number;
  tiersOpen: number;
  patients: number;
  queue: number;
  lettersUnread: number;
  brokerLockedUntil: number;
  /** Prestige progress proxies (no explicit run counter exists). */
  rc: number;
  highWater: string;
  criminalUnlocked: boolean;
}

export function runSnapshot(state: GameState): RunSnapshot {
  return {
    tick: state.run.tick,
    funding: Math.round(state.run.funding),
    rep: Math.round(state.run.reputation),
    heat: Math.round(state.run.heat),
    dea: Math.round(state.run.dea),
    tiersOpen: state.run.availableTiers.length,
    patients: state.run.patients.length,
    queue: state.run.queue.length,
    lettersUnread: state.run.mailbox.arrived.filter((m) => !m.read).length,
    brokerLockedUntil: state.run.brokerLockoutUntil,
    rc: Math.round(state.account.reputationCapital),
    highWater: state.account.highWaterLegit,
    criminalUnlocked: state.run.criminal.unlocked,
  };
}

/**
 * Coarse browser context (v0.12 RFC §3.2/§3.3) — deliberately NOT a
 * fingerprint: device class, viewport, DPR, language, persisted-storage.
 * Guarded so headless/node tests get honest defaults instead of throws.
 */
export interface BrowserBlock {
  device: "mobile" | "desktop";
  viewport: string;
  dpr: number;
  lang: string;
  persisted: boolean | "unknown";
}

export function browserBlock(): BrowserBlock {
  const hasWindow = typeof window !== "undefined";
  const nav = typeof navigator !== "undefined" ? navigator : undefined;
  return {
    device: nav !== undefined && /Mobi|Android|iPhone|iPad/i.test(nav.userAgent) ? "mobile" : "desktop",
    viewport: hasWindow ? `${window.innerWidth}x${window.innerHeight}` : "0x0",
    dpr: hasWindow ? window.devicePixelRatio : 1,
    lang: nav?.language ?? "unknown",
    persisted: persistedCache,
  };
}

/** navigator.storage.persisted() is async; telemetry init resolves it ONCE
 * into this cache so every later payload (feedback AND beacons) carries the
 * real answer instead of a permanent "unknown" (v0.12 review). */
let persistedCache: boolean | "unknown" = "unknown";
export function setPersistedCache(v: boolean): void {
  persistedCache = v;
}

/** The version triple every outbound payload carries. */
export function versionBlock(): { version: string; schema: number; logic: number } {
  return { version: __APP_VERSION__, schema: SAVE.version, logic: LOGIC.version };
}

/** Injected by Vite `define` from package.json (see vite.config.ts). */
declare global {
  const __APP_VERSION__: string;
}
